import {
  ADD_STEP_ALIASES,
  ADD_STEP_PRESET_IDS,
  ADD_STEP_PRESETS,
  AI_STEP_TYPES,
  CONVERSATION_STEP_TYPES,
  isFacingTrigger,
  isFacingType,
  NON_CONVERSATION_TRIGGERS,
  planAtLeast,
  STEP_CATEGORY,
  SYSTEM_STEP_ALLOWLIST,
  UNATTACHED_LOCKED_TYPES,
  UNATTACHED_TRIGGERS,
  WORKFLOW_ACTION_TYPES,
  WORKFLOW_FACING_TYPES,
  WORKFLOW_FLOW_TYPES,
  WORKFLOW_STEP_MIN_PLAN,
  type AddStepPresetId,
  type PlanKey,
  type StepCategory,
  type WorkflowStepType,
  type WorkflowTrigger,
} from "@sagot/constants";
import type { IconName } from "@sagot/ui";
import { STEP_ICONS } from "../step-icons";
import type { DefaultWorkflowInfo } from "../system/default-context";
import { isSystemType, systemStepOffered } from "../system/system-model";
import { isTicketTrigger } from "../../service/service-facts";
import { isServiceType } from "../../service/service-model";
import { serviceDeskBlocked, type ServiceDeskState } from "../../service/use-service-desk";
import { splitKeywords, type SearchTerms } from "./add-step-search";

const B = "engage.workflows";

export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Why a step cannot be added on this trigger. `visitor` and `wait` reuse the copy of the old menu; `system` is a default workflow's allowlist; `service` is a Service Desk step in a workspace without the Service Desk (plan or flag). */
export type TriggerLock = "visitor" | "wait" | "conversation" | "system" | "service";

export interface AddStepEntry {
  /** The step type, or the preset id ("close_conversation"). Unique in the catalog. */
  id: string;
  type: WorkflowStepType;
  preset?: AddStepPresetId;
  category: StepCategory;
  ai: boolean;
  label: string;
  icon: IconName;
  /** The plan the step needs when the workspace is below it. */
  planNeeded: PlanKey | null;
  triggerLock: TriggerLock | null;
  locked: boolean;
}

export interface CatalogContext {
  trigger: WorkflowTrigger;
  /** The workspace plan; unknown (still loading) never locks a step. */
  plan: string | undefined;
  t: Translate;
  /** Set when the workflow is one of the defaults: only the steps a default may hold can be added. */
  defaults?: DefaultWorkflowInfo | null;
  /** Whether the Service Desk can be used here; its steps are locked when the plan or the workspace lacks it (unknown never locks). */
  serviceDesk?: ServiceDeskState;
}

/**
 * Menu order of each list: what authors reach for first, then everything else in the order the type lists declare.
 * Ids are step types or preset ids. A step added to the product later lands after these without being listed.
 */
const ORDER: Readonly<Record<StepCategory, readonly string[]>> = {
  content: [],
  actions: ["route_handoff", "assign_lead_owner", "send_to_subscribed_endpoints", "notify_team", "assign", "add_tags", "add_note", "add_summary_note", "set_attribute", "close_conversation", "notify_staff", "resolve_conversation", "snooze", "remove_tags", "set_status", "set_priority", "set_language", "email_visitor", "update_lead", "send_webhook", "http_request", "apply_sla"],
  flow: ["branch", "apply_rules", "wait", "wait_for_webhook", "run_workflow", "go_to_path", "end"],
};

/** The Service Desk steps in the order of the scope. On a ticket moment they lead the Actions list; anywhere else they follow the older steps. */
const SERVICE_ACTIONS: readonly string[] = ["route_ticket", "assign_ticket", "notify_contact", "set_ticket_state", "add_ticket_note", "reply_to_ticket", "create_ticket", "update_ticket", "link_ticket", "request_csat", "apply_ticket_sla"];

/** What a default workflow may hold (`SYSTEM_STEP_ALLOWLIST`); `assign` only in a default that runs after its event, not at it. */
function systemLocked(type: WorkflowStepType, context: CatalogContext): boolean {
  const info = context.defaults;
  if (!info) return false;
  if (type === "assign" && info.execution === "inline") return true;
  return !SYSTEM_STEP_ALLOWLIST.has(type);
}

function triggerLockOf(type: WorkflowStepType, trigger: WorkflowTrigger, context?: CatalogContext): TriggerLock | null {
  if (context && systemLocked(type, context)) return "system";
  if (context && isServiceType(type) && serviceDeskBlocked(context.serviceDesk ?? "unknown")) return "service";
  if (isFacingType(type) && !isFacingTrigger(trigger)) return "visitor";
  if (UNATTACHED_LOCKED_TYPES.has(type) && UNATTACHED_TRIGGERS.has(trigger)) return "wait";
  if (CONVERSATION_STEP_TYPES.has(type) && NON_CONVERSATION_TRIGGERS.has(trigger)) return "conversation";
  return null;
}

function planNeededBy(type: WorkflowStepType, plan: string | undefined): PlanKey | null {
  const needs = WORKFLOW_STEP_MIN_PLAN[type];
  return needs !== undefined && plan !== undefined && !planAtLeast(plan, needs) ? needs : null;
}

function entryOf(id: string, type: WorkflowStepType, preset: AddStepPresetId | undefined, context: CatalogContext): AddStepEntry {
  const planNeeded = planNeededBy(type, context.plan);
  const triggerLock = triggerLockOf(type, context.trigger, context);
  const presetSpec = preset ? ADD_STEP_PRESETS[preset] : undefined;
  return {
    id,
    type,
    ...(preset ? { preset } : {}),
    category: STEP_CATEGORY[type],
    ai: AI_STEP_TYPES.has(type),
    label: preset ? context.t(`${B}.addStep.presets.${preset}`) : context.t(`${B}.steps.${type}`),
    icon: presetSpec?.icon ?? STEP_ICONS[type],
    planNeeded,
    triggerLock,
    locked: planNeeded !== null || triggerLock !== null,
  };
}

function inMenuOrder(entries: AddStepEntry[], category: StepCategory, trigger: WorkflowTrigger): AddStepEntry[] {
  const order = category === "actions" ? (isTicketTrigger(trigger) ? [...SERVICE_ACTIONS, ...ORDER.actions] : [...ORDER.actions, ...SERVICE_ACTIONS]) : ORDER[category];
  const rank = (entry: AddStepEntry) => {
    const at = order.indexOf(entry.id);
    return at < 0 ? order.length : at;
  };
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => rank(a.entry) - rank(b.entry) || a.index - b.index)
    .map(({ entry }) => entry);
}

/** Every step the menu can add (one entry per step type and per preset), with the gates of this trigger and plan applied. */
export function buildCatalog(context: CatalogContext): AddStepEntry[] {
  // The steps of the default workflows are offered only where they work: `notify_team` anywhere, the others on their one trigger (and, for the two that decide at the call site, only in a default).
  const actions = WORKFLOW_ACTION_TYPES.filter((type) => !isSystemType(type) || systemStepOffered(type, context.trigger, Boolean(context.defaults)));
  const types: readonly WorkflowStepType[] = [...WORKFLOW_FACING_TYPES, ...actions, ...WORKFLOW_FLOW_TYPES];
  const plain = types.map((type) => entryOf(type, type, undefined, context));
  const presets = ADD_STEP_PRESET_IDS.map((preset) => entryOf(preset, ADD_STEP_PRESETS[preset].type, preset, context));
  const all = [...plain, ...presets];
  return [
    ...inMenuOrder(all.filter((entry) => entry.category === "content"), "content", context.trigger),
    ...inMenuOrder(all.filter((entry) => entry.category === "actions"), "actions", context.trigger),
    ...inMenuOrder(all.filter((entry) => entry.category === "flow"), "flow", context.trigger),
  ];
}

/** The words that should find a step: its label, the keywords of the console language and the English aliases. */
export function searchTermsOf(entry: AddStepEntry, t: Translate): SearchTerms {
  const keywords = t(`${B}.addStep.keywords.${entry.id}`, { defaultValue: "" });
  return { label: entry.label, others: [...splitKeywords(keywords), ...(ADD_STEP_ALIASES[entry.id] ?? [])] };
}
